import { useCallback, useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { buyLimitEnrich, fetchLimitsEnrichItemList } from "../../api/limitsEnrichItem";
import { useCurrentUserInfo } from "../../hooks/useCurrentUserInfo";
import { useNotification } from "../Notification/NotificationProvider";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import { formatPricelistPrice } from "../../utils/pricelistFormat";
import {
  hasActivePeriod,
  normalizeTopUpPacks,
  pickTopUpItems,
  topUpTotals,
  type TopUpRow,
  validateTopUpRow,
} from "../../utils/checksTopUp";
import "./ChecksTopUpSection.scss";

const MANUAL_SUCCESS = "Запрос на докупку отправлен и будет рассмотрен администратором.";
const AUTO_APPROVED_SUCCESS = "Докупка проверок успешно активирована.";

export const ChecksTopUpSection = () => {
  const { showNotification } = useNotification();
  const { userInfo, loading: userLoading, resolved } = useCurrentUserInfo();
  const [rows, setRows] = useState<TopUpRow[]>([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [packsById, setPacksById] = useState<Record<string, number>>({});

  const canShow = hasActivePeriod(userInfo);

  useEffect(() => {
    if (!canShow) {
      setReady(true);
      setRows([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setReady(false);

    fetchLimitsEnrichItemList()
      .then((items) => {
        if (cancelled) return;
        setRows(pickTopUpItems(items));
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRows([]);
          setError(getApiErrorMessage(err, "Не удалось загрузить пакеты докупки"));
        }
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [canShow]);

  const packsOf = useCallback(
    (id: string) => normalizeTopUpPacks(packsById[id] ?? 1),
    [packsById]
  );

  const shiftPacks = useCallback((id: string, delta: number) => {
    setPacksById((prev) => ({
      ...prev,
      [id]: normalizeTopUpPacks((prev[id] ?? 1) + delta),
    }));
  }, []);

  const handleBuy = useCallback(
    async (row: TopUpRow) => {
      const packs = packsOf(row.enrichItemId);
      const validationError = validateTopUpRow(row, packs);
      if (validationError) {
        showNotification({ message: validationError, variant: "error" });
        return;
      }

      const totals = topUpTotals(row, packs);
      setBuyingId(row.enrichItemId);
      try {
        const response = await buyLimitEnrich({
          items: [{ enrichItemId: row.enrichItemId, count: totals.count }],
        });

        if (response.paymentType === "bank") {
          if (response.paymentUrl) {
            window.location.assign(response.paymentUrl);
            return;
          }
          showNotification({
            message: "Не удалось получить ссылку на оплату",
            variant: "error",
          });
          return;
        }

        if (response.paymentType === "auto_approved") {
          showNotification({ message: AUTO_APPROVED_SUCCESS, variant: "success" });
          return;
        }

        showNotification({ message: MANUAL_SUCCESS, variant: "success" });
      } catch (err: unknown) {
      } finally {
        setBuyingId(null);
      }
    },
    [packsOf, showNotification]
  );

  if (userLoading || !resolved) {
    return (
      <section className="checks-top-up" aria-label="Докупка проверок">
        <div className="checks-top-up-loading">
          <CircularProgress size="md" />
        </div>
      </section>
    );
  }

  if (!canShow) {
    return null;
  }

  return (
    <section className="checks-top-up" aria-label="Докупка проверок">
      <div className="checks-top-up-head">
        <h2 className="checks-top-up-title">Докупка проверок</h2>
        <p className="checks-top-up-subtitle">
          Доступна при активном периоде тарифа. Количество кратно пакету из каталога.
        </p>
      </div>

      {!ready ? (
        <div className="checks-top-up-loading">
          <CircularProgress size="md" />
        </div>
      ) : error ? (
        <p className="checks-top-up-empty checks-top-up-empty--error">{error}</p>
      ) : rows.length === 0 ? (
        <p className="checks-top-up-empty">Нет доступных пакетов докупки</p>
      ) : (
        <div className="checks-top-up-card">
          <div className="checks-top-up-rows">
            {rows.map((row) => {
              const busy = buyingId === row.enrichItemId;
              const packs = packsOf(row.enrichItemId);
              const totals = topUpTotals(row, packs);

              return (
                <div key={row.checkType} className="checks-top-up-row">
                  <div className="checks-top-up-row-main">
                    <div className="checks-top-up-row-text">
                      <p className="checks-top-up-row-label">{row.label}</p>
                      <p className="checks-top-up-row-meta">
                        {totals.count} проверок · {formatPricelistPrice(totals.price)}
                      </p>
                    </div>
                    <div className="checks-top-up-qty">
                      {packs > 1 ? (
                        <button
                          type="button"
                          className="checks-top-up-qty-btn"
                          aria-label="Уменьшить количество"
                          disabled={buyingId != null}
                          onClick={() => shiftPacks(row.enrichItemId, -1)}
                        >
                          <Minus size={16} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="checks-top-up-qty-btn"
                        aria-label="Увеличить количество"
                        disabled={buyingId != null}
                        onClick={() => shiftPacks(row.enrichItemId, 1)}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary checks-top-up-buy-btn"
                      disabled={buyingId != null}
                      onClick={() => handleBuy(row)}
                    >
                      {busy ? <CircularProgress size="sm" /> : "Купить"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};
